/**
 * Access → Applications
 *
 * The tenant's catalog of apps reachable through the Access Platform, plus
 * Application Groups (V28) — named sets of apps a policy rule can target as one.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Pencil, AppWindow, Boxes, ExternalLink } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import StatCard from '../../components/common/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import AppModal from '../../components/access/AppModal';
import AppGroupModal from '../../components/access/AppGroupModal';
import ZtnaChip from '../../components/access/ZtnaChip';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  type AccessApp,
  type AppGroup,
  type AppGroupData,
  type CreateAppData,
  type SaasProfile,
} from '../../services/access.service';

type Tab = 'apps' | 'groups';

/** Render the routing target in the form that matches the app type. */
function targetOf(app: AccessApp): string {
  if (app.app_type === 'external') {
    const patterns = app.fqdn_patterns ?? [];
    if (patterns.length === 0) return '—';
    return patterns.length <= 2 ? patterns.join(', ') : `${patterns.slice(0, 2).join(', ')} +${patterns.length - 2}`;
  }
  if (app.app_type === 'subnet') return app.target_cidr ?? '—';
  if (!app.target_host) return '—';
  return app.target_port ? `${app.target_host}:${app.target_port}` : app.target_host;
}

const AccessApps = () => {
  const toast = useToast();

  const [tab, setTab] = useState<Tab>('apps');
  const [apps, setApps] = useState<AccessApp[]>([]);
  const [appGroups, setAppGroups] = useState<AppGroup[]>([]);
  const [connectorGroups, setConnectorGroups] = useState<string[]>([]);
  const [saasProfiles, setSaasProfiles] = useState<SaasProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AccessApp | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Group management state.
  const [isGroupAddOpen, setIsGroupAddOpen] = useState(false);
  const [groupEditTarget, setGroupEditTarget] = useState<AppGroup | null>(null);
  const [groupDeleteTarget, setGroupDeleteTarget] = useState<AppGroup | null>(null);
  const [groupDeleting, setGroupDeleting] = useState(false);

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await accessService.listApps({ limit: 200 });
      setApps(result?.items ?? []);
    } catch (err) {
      // Surfaced rather than swallowed: a SuperAdmin with no tenant selected
      // gets a 400 here, and silently showing an empty table hides why.
      setError(err instanceof Error ? err.message : 'Failed to load applications');
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAppGroups = useCallback(async () => {
    try {
      setAppGroups(await accessService.listAppGroups());
    } catch {
      // Non-fatal for the apps tab; the groups tab surfaces the empty state.
      setAppGroups([]);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const groups = await accessService.listConnectorGroups();
      setConnectorGroups((groups ?? []).map((g) => g.connectorGroup));
    } catch {
      // Non-fatal: the group field falls back to free text.
      setConnectorGroups([]);
    }
  }, []);

  const fetchSaas = useCallback(async () => {
    try {
      // Tenant admins receive only enabled profiles from the API.
      setSaasProfiles(await accessService.listSaasCatalog());
    } catch {
      // Non-fatal: the SaaS type simply offers no picks.
      setSaasProfiles([]);
    }
  }, []);

  useEffect(() => {
    void fetchApps();
    void fetchAppGroups();
    void fetchGroups();
    void fetchSaas();
  }, [fetchApps, fetchAppGroups, fetchGroups, fetchSaas]);

  const handleCreate = async (data: CreateAppData) => {
    try {
      await accessService.createApp(data);
      toast.success('Application created', `${data.name} is now in the catalog`);
      setIsAddOpen(false);
      await fetchApps();
    } catch (err) {
      toast.error('Create failed', err instanceof Error ? err.message : 'Could not create the application');
      throw err; // keeps the modal open
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deleteApp(deleteTarget.id);
      toast.success('Application deleted', deleteTarget.name);
      setDeleteTarget(null);
      await Promise.all([fetchApps(), fetchAppGroups()]);
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the application');
    } finally {
      setDeleting(false);
    }
  };

  // Enable/disable an app in place (no modal). A disabled app is authored but
  // not served — the compile step only bundles enabled apps.
  const handleToggle = async (app: AccessApp) => {
    setTogglingId(app.id);
    try {
      await accessService.updateApp(app.id, { enabled: !app.enabled });
      await fetchApps();
      toast.success(app.enabled ? 'Application disabled' : 'Application enabled', app.name);
    } catch (err) {
      toast.error('Could not update the application', err instanceof Error ? err.message : 'Update failed');
    } finally {
      setTogglingId(null);
    }
  };

  const handleGroupSave = async (data: AppGroupData) => {
    try {
      if (groupEditTarget) {
        await accessService.updateAppGroup(groupEditTarget.id, data);
        toast.success('Group updated', data.name);
        setGroupEditTarget(null);
      } else {
        await accessService.createAppGroup(data);
        toast.success('Group created', data.name);
        setIsGroupAddOpen(false);
      }
      await fetchAppGroups();
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : 'Could not save the group');
      throw err; // keeps the modal open
    }
  };

  const handleGroupDelete = async () => {
    if (!groupDeleteTarget) return;
    try {
      setGroupDeleting(true);
      await accessService.deleteAppGroup(groupDeleteTarget.id);
      toast.success('Group deleted', groupDeleteTarget.name);
      setGroupDeleteTarget(null);
      await fetchAppGroups();
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the group');
    } finally {
      setGroupDeleting(false);
    }
  };

  const columns: Column<AccessApp>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (app) => (
        <div>
          <div className="font-medium text-gray-900">{app.display_name || app.name}</div>
          <div className="text-xs text-gray-500">{app.name}</div>
        </div>
      ),
    },
    {
      key: 'app_type',
      label: 'Type',
      sortable: true,
      render: (app) =>
        app.app_type === 'external' ? (
          <span className="inline-flex items-center rounded border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
            External (SaaS)
          </span>
        ) : (
          <span className="text-sm text-gray-700">{app.app_type}</span>
        ),
    },
    {
      key: 'target',
      label: 'Target',
      render: (app) => (
        <span className="inline-flex items-center gap-1.5">
          {targetOf(app)}
          {/* The API's derived launch URL (VSNP-1938): what the client's Open button opens. */}
          {app.launchUrl && (
            <a
              href={app.launchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-primary-600"
              title={`Launch URL (derived): ${app.launchUrl}`}
              aria-label={`Launch URL for ${app.name}`}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </span>
      ),
    },
    {
      key: 'connector_group',
      label: 'Connector Group',
      sortable: true,
      render: (app) =>
        app.app_type === 'external' ? (
          <span className="text-gray-400 italic text-sm">breakout</span>
        ) : (
          <span className="text-sm text-gray-700">{app.connector_group}</span>
        ),
    },
    {
      key: 'tags',
      label: 'Tags',
      render: (app) =>
        app.tags && app.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {app.tags.map((tag) => (
              <span key={tag} className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (app) => (
        <div className="flex items-center gap-3">
          <ToggleSwitch
            checked={!!app.enabled}
            busy={togglingId === app.id}
            onChange={() => handleToggle(app)}
            label={app.enabled ? `Disable ${app.name}` : `Enable ${app.name}`}
            title={app.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
          />
          <button
            type="button"
            className="text-gray-400 hover:text-danger-600"
            title="Delete application"
            onClick={() => setDeleteTarget(app)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const groupColumns: Column<AppGroup>[] = [
    {
      key: 'name',
      label: 'Group',
      sortable: true,
      render: (g) => (
        <div>
          <div className="font-medium text-gray-900">{g.name}</div>
          {g.description && <div className="text-xs text-gray-500">{g.description}</div>}
        </div>
      ),
    },
    {
      key: 'members',
      label: 'Applications',
      render: (g) =>
        g.members.length === 0 ? (
          <span className="text-gray-400 text-sm">empty</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {g.members.slice(0, 6).map((m) => (
              <span key={m.id} className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                {m.name}
              </span>
            ))}
            {g.members.length > 6 && (
              <span className="px-2 py-0.5 text-xs text-gray-500">+{g.members.length - 6}</span>
            )}
          </div>
        ),
    },
    {
      key: 'actions',
      label: '',
      render: (g) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-primary-600"
            title="Edit group"
            onClick={() => setGroupEditTarget(g)}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-danger-600"
            title="Delete group"
            onClick={() => setGroupDeleteTarget(g)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const enabledCount = apps.filter((a) => a.enabled).length;

  const tabButton = (id: Tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
        tab === id
          ? 'border-primary-600 text-primary-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Applications</h1>
            <ZtnaChip />
          </div>
          <p className="text-gray-600 mt-1">Private and SaaS applications governed by the Access Platform</p>
          <p className="text-xs text-gray-400 mt-1">
            Looking for v2 network application definitions? → Network → Applications
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void fetchApps();
              void fetchAppGroups();
            }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {tab === 'apps' ? (
            <button type="button" className="btn-primary" onClick={() => setIsAddOpen(true)}>
              <Plus className="w-4 h-4 mr-2 inline" />
              Add Application
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={() => setIsGroupAddOpen(true)}>
              <Plus className="w-4 h-4 mr-2 inline" />
              New Group
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <StatCard title="Applications" value={apps.length} icon={AppWindow} color="primary" loading={loading} />
        <StatCard title="Enabled" value={enabledCount} icon={AppWindow} color="success" loading={loading} />
        <StatCard title="Groups" value={appGroups.length} icon={Boxes} color="info" loading={loading} />
      </div>

      <div className="border-b border-gray-200">
        {tabButton('apps', 'Applications')}
        {tabButton('groups', 'Groups')}
      </div>

      {tab === 'apps' ? (
        <div className="card">
          <DataTable
            columns={columns}
            data={apps}
            loading={loading}
            searchPlaceholder="Search applications…"
            emptyMessage="No applications defined yet"
          />
        </div>
      ) : (
        <div className="card">
          <p className="text-sm text-gray-500 mb-3">
            Groups let a policy rule target many apps at once. A rule targeting a group is expanded to its member
            apps when the bundle is compiled.
          </p>
          <DataTable
            columns={groupColumns}
            data={appGroups}
            searchPlaceholder="Search groups…"
            emptyMessage="No application groups yet — create one to target several apps with a single rule"
          />
        </div>
      )}

      <AppModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSave={handleCreate}
        connectorGroups={connectorGroups}
        saasProfiles={saasProfiles}
      />

      {(isGroupAddOpen || groupEditTarget) && (
        <AppGroupModal
          key={groupEditTarget?.id ?? 'new'}
          isOpen
          group={groupEditTarget}
          apps={apps}
          onClose={() => (groupEditTarget ? setGroupEditTarget(null) : setIsGroupAddOpen(false))}
          onSave={handleGroupSave}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete application"
        message={`Delete '${deleteTarget?.name}'? Policy rules that reference it by id must be updated first.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />

      <ConfirmDialog
        isOpen={!!groupDeleteTarget}
        onClose={() => setGroupDeleteTarget(null)}
        onConfirm={handleGroupDelete}
        title="Delete application group"
        message={`Delete group '${groupDeleteTarget?.name}'? Policy rules that reference it must be updated first. Member apps are not deleted.`}
        confirmText="Delete"
        variant="danger"
        isLoading={groupDeleting}
      />
    </div>
  );
};

export default AccessApps;
